<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';

const props = defineProps({
  banks: { type: Array, default: () => [] },
  errors: { type: Object, default: () => ({}) },
  options: { type: Array, default: () => [] },
  participant: { type: Object, required: true },
});

const emit = defineEmits(['editPayment', 'update']);
const { t } = useI18n();
const optionLabel = option => option.label || option.name || option.nome;
const formattedAmount = computed(() =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(props.participant.amount || 0))
);
const bankName = computed(
  () =>
    props.participant.bank_name ||
    optionLabel(
      props.banks.find(
        bank => String(bank.id) === String(props.participant.bank_id)
      ) || {}
    )
);
const paymentChips = computed(() => {
  const participant = props.participant;
  const chips = [String(participant.payment_type || '').toUpperCase()].filter(
    Boolean
  );
  if (participant.payment_type === 'pix' && participant.pix_key) {
    chips.push(participant.pix_key);
  }
  if (participant.payment_type === 'banco') {
    if (bankName.value) chips.push(bankName.value.toUpperCase());
    if (participant.branch) {
      chips.push(
        t('SALES.COMMISSION.PARTICIPANTS.BRANCH_CHIP', {
          value: participant.branch,
        })
      );
    }
    if (participant.account_number) {
      chips.push(
        t('SALES.COMMISSION.PARTICIPANTS.ACCOUNT_CHIP', {
          value: participant.account_number,
        })
      );
    }
  }
  return chips;
});
</script>

<template>
  <div class="py-5">
    <div class="grid gap-4 lg:grid-cols-3">
      <LaisField
        :label="t('SALES.COMMISSION.FIELDS.NAME')"
        :error="errors.name"
        required
      >
        <select
          :value="participant.user_id"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
          @change="emit('update', 'user_id', $event.target.value)"
        >
          <option value="">{{ t('SALES.COMMISSION.COMMON.SELECT') }}</option>
          <option v-for="option in options" :key="option.id" :value="option.id">
            {{ optionLabel(option) }}
          </option>
        </select>
      </LaisField>
      <LaisField
        :label="t('SALES.COMMISSION.FIELDS.PERCENTAGE')"
        :error="errors.percentage"
        required
      >
        <input
          :value="participant.percentage"
          type="number"
          min="0"
          max="100"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
          @input="emit('update', 'percentage', $event.target.value)"
        />
      </LaisField>
      <LaisField :label="t('SALES.COMMISSION.FIELDS.AMOUNT')" required>
        <input
          :value="formattedAmount"
          disabled
          class="min-h-10 rounded-lais border border-lais-border bg-lais-bg-soft px-3 text-sm"
        />
      </LaisField>
    </div>
    <div class="mt-3 flex flex-wrap items-center gap-2">
      <button
        type="button"
        class="inline-flex min-h-9 items-center gap-2 rounded-lais px-2 text-sm font-semibold text-lais-primary hover:bg-lais-primary-50"
        @click="emit('editPayment')"
      >
        <span class="i-lucide-pencil size-4" aria-hidden="true" />
        {{ t('SALES.COMMISSION.ACTIONS.EDIT_PAYMENT') }}
      </button>
      <span
        v-for="chip in paymentChips"
        :key="chip"
        class="rounded-full bg-lais-bg-soft px-2.5 py-1 text-xs font-semibold text-lais-text-muted"
      >
        {{ chip }}
      </span>
    </div>
    <p v-if="errors.payment" class="mt-2 text-sm text-lais-danger">
      {{ errors.payment }}
    </p>
  </div>
</template>
